<template>
  <div class="payment-page">
    <div class="container py-5">
      <div class="payment-card mx-auto">
        <div class="text-center mb-4">
          <div class="secure-badge d-flex align-items-center justify-content-center gap-2 mb-3 mx-auto" style="width: fit-content;">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#28a745" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            <span class="secure-text">SSL SECURE</span>
          </div>
          <h1 class="page-title mb-1">{{ t('payment_secure.title') }}</h1>
          <p class="small text-muted letter-spacing-1 text-uppercase">Transazione sicura e criptata</p>
        </div>

        <div class="payment-method-header mb-4 text-center">
          <div class="payment-icons d-flex gap-3 align-items-center justify-content-center">
            <img :src="visaIcon" alt="Visa" width="35" class="payment-icon">
            <img :src="mastercardIcon" alt="Mastercard" width="32" class="payment-icon">
            <div class="divider-v"></div>
            <img :src="stripeIcon" alt="Stripe" width="45" class="payment-icon opacity-75">
          </div>
        </div>
        
        <!-- Order Summary Mini -->
        <div class="order-summary-box mb-4">
          <div class="summary-item px-3 pt-3">
            <span class="summary-label">{{ t('payment_secure.order_number') }}</span>
            <span class="summary-value fw-bold letter-spacing-05">{{ orderNumber || orderId }}</span>
          </div>
          <div class="summary-divider my-3 mx-3"></div>
          <div class="summary-item px-3 pb-3 total-item">
            <span class="summary-label fw-bold text-dark" style="font-size: 10px;">{{ t('payment_secure.total_to_pay') }}</span>
            <span class="summary-value total-amount text-dark">{{ formatPrice(totalAmount) }}</span>
          </div>
        </div>

        <div v-if="paymentData && isStripeReady">
          <div v-if="paymentError" class="error-message mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="me-1"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            {{ paymentError }}
          </div>
          <StripeCheckout
            :publishable-key="paymentData.publishableKey"
            :client-secret="paymentData.clientSecret"
            :order-id="orderId"
            :total-amount="paymentData.totalAmount"
            :merchant-name="paymentData.merchantName"
            :email="authStore.user?.email || ''"
            pay-button-label="PAGA ORA"
            @success="onPaymentSuccess"
            @error="onPaymentError"
            @cancel="onPaymentCancel"
          />
        </div>
        <div v-else-if="!isStripeReady && !paymentError" class="text-center py-4">
          <div class="spinner-border text-dark mb-3" role="status"></div>
          <p class="small text-muted mb-0">Caricamento modulo di pagamento...</p>
        </div>
        <div v-else-if="paymentError && !paymentData" class="text-center py-4">
          <div class="error-message mb-3 mx-auto" style="max-width: 300px;">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="me-1"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
            {{ paymentError }}
          </div>
          <button class="btn-pay-now-premium" @click="initializePayment" :disabled="isProcessing">
            <span v-if="isProcessing" class="spinner-border spinner-border-sm me-2"></span>
            {{ isProcessing ? 'Riprova...' : 'RIPROVA PAGAMENTO' }}
          </button>
        </div>

        <!-- Security Trust Badges -->
        <div class="trust-footer mt-5 pt-4 border-top">
          <div class="d-flex justify-content-center align-items-center gap-3 mb-3">
            <img :src="stripeIcon" alt="Stripe" height="20" class="brand-logo-footer">
            <div class="divider-v"></div>
            <div class="ssl-badge d-flex align-items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#28a745" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              <span class="fw-bold" style="font-size: 10px; color: #28a745; letter-spacing: 1px;">SSL SECURE</span>
            </div>
          </div>
          <p class="text-center text-muted mb-0" style="font-size: 10px; letter-spacing: 0.5px;">
            I tuoi dati finanziari non vengono memorizzati sui nostri server.<br>
            Transazioni elaborate tramite circuiti certificati PCI-DSS.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useLanguage } from '@/composables/useLanguage'
import { useToast } from '@/composables/useToast'
import { useConfigStore } from '@/stores/configStore'
import { useAuthStore } from '@/stores/authStore'
import { useCart } from '@/composables/useCart'
import { checkoutService } from '@/services/checkoutService'
import StripeCheckout from '@/components/StripeCheckout.vue'

// Payment Icons
const visaIcon = new URL('../assets/icons/payments/visa-svgrepo-com.svg', import.meta.url).href
const mastercardIcon = new URL('../assets/icons/payments/mastercard-full-svgrepo-com.svg', import.meta.url).href
const stripeIcon = new URL('../assets/icons/payments/stripe-svgrepo-com.svg', import.meta.url).href

const route = useRoute()
const router = useRouter()
const { t } = useLanguage()
const toast = useToast()
const configStore = useConfigStore()
const authStore = useAuthStore()
const { clearCart } = useCart()

const isGuest = computed(() => !authStore.isAuthenticated)

const orderId = ref(route.query.orderId)
const totalAmount = ref(parseFloat(route.query.total) || 0)
const orderNumber = ref(route.query.orderNumber || '')
const guestTrackingToken = ref(route.query.guestTrackingToken || null)
const sessionId = ref(route.query.session_id || null)

const isProcessing = ref(false)
const isStripeReady = ref(false)
const isWalletAvailable = ref(false)
const paymentError = ref('')
const paymentData = ref(null)



const formatPrice = (price) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(price)
}

const generateRecaptchaToken = async () => {
  if (!configStore.recaptchaSiteKey) return ''
  
  const waitForRecaptcha = () => {
    return new Promise((resolve) => {
      if (window.grecaptcha && window.grecaptcha.enterprise) {
        resolve(true)
        return
      }
      
      let attempts = 0
      const interval = setInterval(() => {
        attempts++
        if (window.grecaptcha && window.grecaptcha.enterprise) {
          clearInterval(interval)
          resolve(true)
        } else if (attempts > 50) {
          clearInterval(interval)
          resolve(false)
        }
      }, 100)
    })
  }

  const isRecaptchaReady = await waitForRecaptcha()
  if (!isRecaptchaReady) return ''

  return await new Promise((resolve) => {
    window.grecaptcha.enterprise.ready(async () => {
      try {
        const token = await window.grecaptcha.enterprise.execute(configStore.recaptchaSiteKey, { action: 'payment' })
        resolve(token)
      } catch (e) {
        console.error('[RECAPTCHA] Execute error:', e)
        resolve('')
      }
    })
  })
}

const initializePayment = async () => {
  if (!orderId.value) {
    toast.error('Dati ordine mancanti.')
    router.push({ name: 'Cart' })
    return
  }

  isProcessing.value = true
  paymentError.value = ''

  try {
    const recaptchaToken = await generateRecaptchaToken()
    if (!recaptchaToken) {
      throw new Error("Verifica di sicurezza (reCAPTCHA) non disponibile. Ricarica la pagina. Se il problema persiste, contatta l'assistenza.")
    }

    const paymentInitResponse = await checkoutService.initializePayment(orderId.value, guestTrackingToken.value, recaptchaToken)
    const paymentDataRaw = paymentInitResponse.data || paymentInitResponse
    const { publishableKey, clientSecret, paymentMethodTypes, merchantName, sessionId: initSessionId } = paymentDataRaw

    if (!publishableKey || !clientSecret) {
      throw new Error("Errore: Il server non ha restituito le chiavi necessarie per il pagamento.")
    }

    paymentData.value = {
      publishableKey,
      clientSecret,
      paymentMethodTypes,
      merchantName,
      totalAmount: Math.round(totalAmount.value * 100)
    }

    if (initSessionId) {
      sessionId.value = initSessionId
    }

    await nextTick()
    isStripeReady.value = true

  } catch (error) {
    let errorMessage = t('apiCodes.SYSTEM_ERROR')
    const code = error?.response?.data?.code
    if (code) {
      errorMessage = t('apiCodes.' + code)
    } else if (error?.response?.status === 400 && error.response?.data?.message) {
      if (error.response.data.message.includes('Verifica di sicurezza')) {
        errorMessage = t('apiCodes.PAYMENT_RECAPTCHA_REQUIRED')
      } else {
        errorMessage = error.response.data.message
      }
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message
    } else if (error.message) {
      errorMessage = error.message
    }
    paymentError.value = errorMessage
    toast.error(errorMessage)
  } finally {
    isProcessing.value = false
  }
}

const onPaymentSuccess = async ({ orderId: id }) => {
  toast.success('Pagamento completato con successo!')
  await clearCart()
  const query = { orderId: id }
  if (guestTrackingToken.value) {
    query.guestTrackingToken = guestTrackingToken.value
  }
  if (sessionId.value) {
    query.session_id = sessionId.value
  }
  router.push({ name: 'CheckoutSuccess', query })
}

const onPaymentError = (err) => {
  console.error('[PaymentPage] Payment error:', err)
  paymentError.value = err?.message || 'Pagamento non completato. Riprova.'
  paymentData.value = null
  isStripeReady.value = false
  sessionId.value = null
}

const onPaymentCancel = () => {
  orderId.value = null
  paymentData.value = null
  isStripeReady.value = false
  guestTrackingToken.value = null
  paymentError.value = null
  sessionId.value = null
}

onMounted(() => {
  initializePayment()
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

.payment-page {
  background: #f8f8f8;
  min-height: 90vh;
  display: flex;
  align-items: center;
  font-family: 'Inter', sans-serif;
}

.payment-card {
  max-width: 420px;
  background: #fff;
  padding: 40px;
  border: 1px solid #eee;
  border-radius: 2px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.04);
  margin: 0 auto;
}

.secure-badge {
  background: #f0fff4;
  padding: 4px 10px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 20px;
}

.secure-text {
  font-family: 'Inter', sans-serif;
  font-size: 9px;
  font-weight: 800;
  color: #28a745;
  letter-spacing: 1px;
}

.page-title {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 800;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #000;
  margin-bottom: 8px;
}

.payment-subtitle {
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  color: #888;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 30px;
}

.payment-icon {
  transition: all 0.3s ease;
  opacity: 1;
}

.payment-card:hover .payment-icon {
  transform: scale(1.05);
}

.order-summary-box {
  background: #f8f8f8;
  padding: 20px;
  border: 1px solid #eee;
  border-radius: 2px;
  margin-bottom: 25px;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
}

.summary-label {
  font-family: 'Inter', sans-serif;
  font-size: 9px;
  font-weight: 700;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.summary-value {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: #000;
  font-weight: 600;
}

.summary-divider {
  height: 1px;
  background: #eee;
  margin: 15px 0;
}

.total-amount {
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 900;
  letter-spacing: -0.5px;
  color: #000;
}

.form-label-premium {
  font-family: 'Inter', sans-serif;
  display: block;
  font-size: 10px;
  font-weight: 700;
  color: #111;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin-bottom: 8px;
}

.form-control-premium {
  font-family: 'Inter', sans-serif;
  width: 100%;
  border: 1px solid #e0e0e0;
  padding: 14px;
  font-size: 14px;
  border-radius: 0;
  transition: all 0.2s;
  background: #fff;
}

.form-control-premium:focus {
  outline: none;
  border-color: #000;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
}

.stripe-card-input {
  background: #fff;
  border: 1px solid #e0e0e0 !important;
  padding: 12px !important;
  transition: all 0.2s ease;
  border-radius: 0;
}

.stripe-card-input.StripeElement--focus {
  border-color: #000 !important;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
}

.error-message {
  background: #fff5f5;
  border-left: 3px solid #fa755a;
  padding: 12px 16px;
  font-size: 13px;
  color: #fa755a;
  border-radius: 0 4px 4px 0;
  animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both;
  box-shadow: 0 2px 4px rgba(250, 117, 90, 0.1);
  margin-bottom: 20px;
}

@keyframes shake {
  10%, 90% { transform: translate3d(-1px, 0, 0); }
  20%, 80% { transform: translate3d(2px, 0, 0); }
  30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
  40%, 60% { transform: translate3d(4px, 0, 0); }
}

.btn-pay-now-premium {
  font-family: 'Inter', sans-serif;
  background: #000;
  color: #fff;
  border: none;
  padding: 18px;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 2px;
  transition: all 0.3s;
  border-radius: 0;
  position: relative;
  overflow: hidden;
}

.btn-pay-now-premium:hover:not(:disabled) {
  background: #222;
  transform: translateY(-1px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.btn-pay-now-premium:disabled {
  background: #e0e0e0;
  color: #a0a0a0;
  cursor: not-allowed;
}

.divider-v {
  width: 1px;
  height: 15px;
  background: #ddd;
}

.trust-footer {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #eee;
  text-align: center;
}

.trust-footer p {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  color: #888;
  letter-spacing: 0.5px;
  line-height: 1.6;
}

.ssl-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.ssl-badge span {
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 700;
  color: #28a745;
  letter-spacing: 1px;
}

.brand-logo-footer {
  height: 20px;
  opacity: 0.7;
  transition: opacity 0.3s;
}

.brand-logo-footer:hover {
  opacity: 1;
}

.payment-request-button {
  margin-bottom: 20px;
}

@media (max-width: 576px) {
  .payment-card {
    padding: 30px 20px;
    border: none;
    box-shadow: none;
  }
}
</style>
